import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Plus,
  Layers,
  ListTodo,
  Calendar,
  MapPin,
  MoreVertical,
  CheckCircle2,
  Circle,
  Clock,
  Sparkles,
  Download,
  FileText,
  FileSpreadsheet,
  Shield,
  Loader2,
  Mail,
  Send,
  Users,
  BarChart3,
  Activity,
  TrendingUp,
  AlertTriangle,
  FolderKanban,
  MessageSquare,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";

export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  const [phaseForm, setPhaseForm] = useState({ name: "", description: "", start_date: "", end_date: "" });
  const [activityForm, setActivityForm] = useState({ name: "", description: "", start_date: "", end_date: "", estimated_hours: 0 });
  const [taskForm, setTaskForm] = useState({ name: "", description: "", priority: "medium", due_date: "" });
  const [exporting, setExporting] = useState(null);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [weeklyEnabled, setWeeklyEnabled] = useState(false);
  const [weeklyLastSent, setWeeklyLastSent] = useState(null);
  const [weeklyRecipients, setWeeklyRecipients] = useState([]);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [weeklyLogs, setWeeklyLogs] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const commentInputRef = useRef(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addMemberRole, setAddMemberRole] = useState("worker");

  useEffect(() => {
    fetchProject();
    fetchPhases();
    fetchActivities();
    fetchTasks();
    fetchWeeklySettings();
    fetchWeeklyRecipients();
    fetchSchedulerStatus();
    fetchWeeklyLogs();
    fetchDashboardData();
    fetchActivityLog();
    fetchProjectMembers();
    fetchAvailableUsers();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setProject(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch project:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhases = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/phases`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setPhases(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch phases:", err);
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setActivities(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch activities:", err);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setTasks(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    }
  };

  const fetchWeeklySettings = async () => {
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/weekly-report/settings`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setWeeklyEnabled(data.enabled || false);
        setWeeklyLastSent(data.last_sent || null);
      }
    } catch (err) {
      console.error("Failed to fetch weekly settings:", err);
    }
  };

  const fetchWeeklyRecipients = async () => {
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/weekly-report/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setWeeklyRecipients(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch weekly recipients:", err);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const res = await fetch(`${API}/reports/weekly-report/scheduler-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) setSchedulerStatus(await res.json());
    } catch (err) {
      console.error("Failed to fetch scheduler status:", err);
    }
  };

  const fetchWeeklyLogs = async () => {
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/weekly-report/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) setWeeklyLogs(await res.json());
    } catch (err) {
      console.error("Failed to fetch weekly logs:", err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) setDashboardData(await res.json());
    } catch (err) { console.error("Failed to fetch dashboard:", err); }
  };

  const fetchActivityLog = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/activity-log?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) setActivityLog(await res.json());
    } catch (err) { console.error("Failed to fetch activity log:", err); }
  };

  const fetchProjectMembers = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/members`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) setProjectMembers(await res.json());
    } catch (err) { console.error("Failed to fetch members:", err); }
  };

  const fetchAvailableUsers = async () => {
    try {
      const res = await fetch(`${API}/available-users`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) setAvailableUsers(await res.json());
    } catch (err) { console.error("Failed to fetch available users:", err); }
  };

  const addProjectMember = async (userId) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ user_id: userId, role: addMemberRole })
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Medarbetare tillagd!" : "Member added!");
        setAddMemberSearch("");
        fetchProjectMembers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === "sv" ? "Kunde inte lägga till" : "Failed to add"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const removeProjectMember = async (userId) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/members/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Medarbetare borttagen" : "Member removed");
        fetchProjectMembers();
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte ta bort" : "Failed to remove");
    }
  };

  const filteredAvailableUsers = availableUsers.filter(u =>
    !projectMembers.some(m => m.user_id === u.user_id) &&
    u.user_id !== user?.user_id &&
    (u.name?.toLowerCase().includes(addMemberSearch.toLowerCase()) || u.email?.toLowerCase().includes(addMemberSearch.toLowerCase()))
  );

  const openTaskDetail = async (task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
    setComments([]);
    setNewComment("");
    setCommentsLoading(true);
    setMentionOpen(false);
    try {
      const [commentsRes, usersRes] = await Promise.all([
        fetch(`${API}/tasks/${task.task_id}/comments`, {
          headers: { Authorization: `Bearer ${token}` }, credentials: "include"
        }),
        fetch(`${API}/projects/${projectId}/mentionable-users`, {
          headers: { Authorization: `Bearer ${token}` }, credentials: "include"
        })
      ]);
      if (commentsRes.ok) setComments(await commentsRes.json());
      if (usersRes.ok) setMentionableUsers(await usersRes.json());
    } catch (err) { console.error("Failed to fetch task details:", err); }
    finally { setCommentsLoading(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTask) return;
    setCommentSubmitting(true);
    try {
      const res = await fetch(`${API}/tasks/${selectedTask.task_id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ text: newComment.trim() })
      });
      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [...prev, comment]);
        setNewComment("");
        toast.success(language === "sv" ? "Kommentar tillagd" : "Comment added");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte lägga till kommentar" : "Failed to add comment");
    } finally { setCommentSubmitting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API}/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        toast.success(language === "sv" ? "Kommentar raderad" : "Comment deleted");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte radera" : "Failed to delete");
    }
  };

  const handleCommentInput = (e) => {
    const val = e.target.value;
    setNewComment(val);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([a-zA-ZåäöÅÄÖ\w\s]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionOpen(true);
      setMentionIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const filteredMentions = mentionableUsers.filter(u =>
    u.name.toLowerCase().includes(mentionQuery) && u.user_id !== user?.user_id
  );

  const insertMention = (mentionUser) => {
    const input = commentInputRef.current;
    if (!input) return;
    const cursorPos = input.selectionStart;
    const textBefore = newComment.slice(0, cursorPos);
    const textAfter = newComment.slice(cursorPos);
    const atIdx = textBefore.lastIndexOf("@");
    const before = textBefore.slice(0, atIdx);
    const inserted = `@${mentionUser.name} `;
    setNewComment(before + inserted + textAfter);
    setMentionOpen(false);
    setTimeout(() => {
      input.focus();
      const newPos = before.length + inserted.length;
      input.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleCommentKeyDown = (e) => {
    if (!mentionOpen || filteredMentions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIndex(i => Math.min(i + 1, filteredMentions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(filteredMentions[mentionIndex]);
    } else if (e.key === "Escape") {
      setMentionOpen(false);
    }
  };

  const renderCommentText = (text) => {
    const mentionNames = mentionableUsers.map(u => u.name);
    const regex = new RegExp(`(@(?:${mentionNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("|")}))`, "g");
    if (mentionNames.length === 0) return text;
    const parts = text.split(regex);
    return parts.map((part, i) => {
      if (part.startsWith("@") && mentionNames.some(n => part === `@${n}`)) {
        return <span key={i} className="text-blue-600 font-medium bg-blue-50 rounded px-0.5">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const toggleWeeklyReport = async (enabled) => {
    setWeeklyLoading(true);
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/weekly-report/settings?enabled=${enabled}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setWeeklyEnabled(enabled);
        toast.success(enabled
          ? (language === "sv" ? "Veckorapport aktiverad" : "Weekly report enabled")
          : (language === "sv" ? "Veckorapport avaktiverad" : "Weekly report disabled"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte ändra inställning" : "Failed to update setting");
    } finally {
      setWeeklyLoading(false);
    }
  };

  const sendWeeklyReportNow = async () => {
    setSendingWeekly(true);
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/weekly-report/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        const count = data.sent_to?.length || 0;
        toast.success(language === "sv"
          ? `Veckorapport skickad till ${count} mottagare!`
          : `Weekly report sent to ${count} recipients!`);
        setWeeklyLastSent(new Date().toISOString());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === "sv" ? "Kunde inte skicka" : "Failed to send"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte skicka veckorapport" : "Failed to send weekly report");
    } finally {
      setSendingWeekly(false);
    }
  };

  const createPhase = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/projects/${projectId}/phases`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(phaseForm)
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Etapp skapad!" : "Phase created!");
        setPhaseDialogOpen(false);
        setPhaseForm({ name: "", description: "", start_date: "", end_date: "" });
        fetchPhases();
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const createActivity = async (e) => {
    e.preventDefault();
    if (!selectedPhase) return;
    try {
      const res = await fetch(`${API}/phases/${selectedPhase}/activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(activityForm)
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Aktivitet skapad!" : "Activity created!");
        setActivityDialogOpen(false);
        setActivityForm({ name: "", description: "", start_date: "", end_date: "", estimated_hours: 0 });
        fetchActivities();
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!selectedActivity) return;
    try {
      const res = await fetch(`${API}/activities/${selectedActivity}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(taskForm)
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Uppgift skapad!" : "Task created!");
        setTaskDialogOpen(false);
        setTaskForm({ name: "", description: "", priority: "medium", due_date: "" });
        fetchTasks();
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const toggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === "completed" ? "pending" : "completed";
    try {
      await fetch(`${API}/tasks/${taskId}?status=${newStatus}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      fetchTasks();
    } catch (err) {
      console.error("Failed to update task:", err);
    }
  };

  const generateAISummary = async () => {
    setLoadingAI(true);
    setAiAnalysis(null);
    setAiSummary(null);
    try {
      const res = await fetch(`${API}/projects/${projectId}/ai/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data.analysis);
        setAiSummary(data.analysis?.summary || "");
        toast.success(language === "sv" ? "AI-analys klar!" : "AI analysis complete!");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === "sv" ? "Kunde inte generera analys" : "Failed to generate analysis"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte generera analys" : "Failed to generate analysis");
    } finally {
      setLoadingAI(false);
    }
  };

  const downloadReport = async (endpoint, filename, exportKey) => {
    setExporting(exportKey);
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === "sv" ? "Rapport nedladdad!" : "Report downloaded!");
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte exportera" : "Export failed");
    } finally {
      setExporting(null);
    }
  };

  const downloadPeriodReport = async () => {
    if (!periodStart || !periodEnd) {
      toast.error(language === "sv" ? "Välj start- och slutdatum" : "Select start and end date");
      return;
    }
    setExporting("period");
    try {
      const res = await fetch(`${API}/reports/projects/${projectId}/period-pdf?start_date=${periodStart}&end_date=${periodEnd}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `periodrapport_${periodStart}_${periodEnd}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === "sv" ? "Periodrapport nedladdad!" : "Period report downloaded!");
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte exportera" : "Export failed");
    } finally {
      setExporting(null);
    }
  };

  const completedTasks = tasks.filter(t => t.status === "completed").length;
  const taskProgress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  const statusColors = {
    planned: "bg-slate-100 text-slate-800",
    active: "bg-blue-100 text-blue-800",
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800"
  };

  const priorityColors = {
    low: "bg-slate-100 text-slate-600",
    medium: "bg-blue-100 text-blue-600",
    high: "bg-amber-100 text-amber-600",
    critical: "bg-red-100 text-red-600"
  };

  const getTimeAgo = (dateStr, lang) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return lang === "sv" ? "Just nu" : "Just now";
    if (mins < 60) return lang === "sv" ? `${mins} min sedan` : `${mins}m ago`;
    if (hours < 24) return lang === "sv" ? `${hours} tim sedan` : `${hours}h ago`;
    if (days < 7) return lang === "sv" ? `${days} dagar sedan` : `${days}d ago`;
    return d.toLocaleDateString(lang === "sv" ? "sv-SE" : "en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="project-detail-page">
        {/* Header */}
        <div>
          <Link to="/projects" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            {language === "sv" ? "Alla projekt" : "All projects"}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  {project?.name}
                </h1>
                <Badge className={statusColors[project?.status] || statusColors.active}>
                  {project?.status}
                </Badge>
              </div>
              {project?.project_number && (
                <p className="text-slate-500 font-mono mt-1">{project.project_number}</p>
              )}
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                {project?.address && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {project.address}
                  </span>
                )}
                {project?.budget > 0 && (
                  <span className="font-medium">
                    {project.budget.toLocaleString('sv-SE')} SEK
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={generateAISummary} disabled={loadingAI} data-testid="ai-summary-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              {loadingAI 
                ? (language === "sv" ? "Genererar..." : "Generating...") 
                : (language === "sv" ? "AI Sammanfattning" : "AI Summary")}
            </Button>
          </div>
        </div>

        {/* AI Analysis Panel */}
        {aiAnalysis && (
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50" data-testid="ai-analysis-panel">
            <CardContent className="p-5 space-y-4">
              {/* Header with health score */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    AI {language === "sv" ? "Projektanalys" : "Project Analysis"}
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{language === "sv" ? "Projekthälsa" : "Health"}</span>
                  <div className={`px-2.5 py-1 rounded-full text-sm font-bold ${
                    aiAnalysis.health_score >= 75 ? "bg-green-100 text-green-700" :
                    aiAnalysis.health_score >= 50 ? "bg-yellow-100 text-yellow-700" :
                    "bg-red-100 text-red-700"
                  }`} data-testid="ai-health-score">
                    {aiAnalysis.health_score}/100
                  </div>
                </div>
              </div>

              {/* Summary */}
              <p className="text-sm text-slate-700" data-testid="ai-summary-text">{aiAnalysis.summary}</p>

              {/* Risks & Recommendations grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Risks */}
                {aiAnalysis.risks?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      {language === "sv" ? "Identifierade risker" : "Identified Risks"}
                    </h5>
                    {aiAnalysis.risks.map((risk, i) => (
                      <div key={i} className="bg-white/70 rounded-lg p-3 border border-red-100" data-testid={`ai-risk-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-800">{risk.title}</span>
                          <Badge className={
                            risk.severity === "high" ? "bg-red-100 text-red-700" :
                            risk.severity === "medium" ? "bg-yellow-100 text-yellow-700" :
                            "bg-slate-100 text-slate-600"
                          }>{risk.severity}</Badge>
                        </div>
                        <p className="text-xs text-slate-600">{risk.description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommendations */}
                {aiAnalysis.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" />
                      {language === "sv" ? "Rekommendationer" : "Recommendations"}
                    </h5>
                    {aiAnalysis.recommendations.map((rec, i) => (
                      <div key={i} className="bg-white/70 rounded-lg p-3 border border-emerald-100" data-testid={`ai-rec-${i}`}>
                        <span className="text-sm font-medium text-slate-800 block mb-1">{rec.title}</span>
                        <p className="text-xs text-slate-600">{rec.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Overview */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">
                {language === "sv" ? "Projektframsteg" : "Project Progress"}
              </span>
              <span className="text-sm text-slate-600">{taskProgress}%</span>
            </div>
            <Progress value={taskProgress} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>{completedTasks} {language === "sv" ? "avklarade" : "completed"}</span>
              <span>{tasks.length - completedTasks} {language === "sv" ? "kvar" : "remaining"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="w-full sm:w-auto flex-wrap">
            <TabsTrigger value="overview" className="flex-1 sm:flex-none" data-testid="dashboard-tab">
              {language === "sv" ? "Dashboard" : "Dashboard"}
            </TabsTrigger>
            <TabsTrigger value="phases" className="flex-1 sm:flex-none">
              {language === "sv" ? "Etapper" : "Phases"}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex-1 sm:flex-none">
              {language === "sv" ? "Uppgifter" : "Tasks"}
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex-1 sm:flex-none" data-testid="activity-tab">
              {language === "sv" ? "Aktivitet" : "Activity"}
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex-1 sm:flex-none" data-testid="reports-tab">
              {language === "sv" ? "Rapporter" : "Reports"}
            </TabsTrigger>
            <TabsTrigger value="team" className="flex-1 sm:flex-none" data-testid="team-tab">
              <Users className="w-4 h-4 mr-1" />
              {language === "sv" ? "Team" : "Team"}
              {projectMembers.length > 0 && (
                <Badge className="ml-1.5 bg-slate-200 text-slate-700 text-xs px-1.5 py-0">{projectMembers.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{language === "sv" ? "Uppgifter" : "Tasks"}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{dashboardData?.tasks?.completed || 0}/{dashboardData?.tasks?.total || 0}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
                  </div>
                  {(dashboardData?.tasks?.total || 0) > 0 && (
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.round(dashboardData.tasks.completed / dashboardData.tasks.total * 100)}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{language === "sv" ? "Budget" : "Budget"}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{dashboardData?.budget?.pct || 0}%</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg"><BarChart3 className="w-5 h-5 text-blue-600" /></div>
                  </div>
                  {(dashboardData?.budget?.total || 0) > 0 && (
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${dashboardData.budget.pct > 90 ? 'bg-red-500' : dashboardData.budget.pct > 70 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, dashboardData.budget.pct)}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{language === "sv" ? "Timmar" : "Hours"}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{dashboardData?.hours?.toFixed(0) || 0}h</p>
                    </div>
                    <div className="p-2 bg-purple-100 rounded-lg"><Clock className="w-5 h-5 text-purple-600" /></div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{language === "sv" ? "Avvikelser" : "Incidents"}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{dashboardData?.incidents?.open || 0}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{dashboardData?.incidents?.total || 0} {language === "sv" ? "totalt" : "total"}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{language === "sv" ? "Ritningar" : "Drawings"}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{dashboardData?.drawings || 0}</p>
                    </div>
                    <div className="p-2 bg-amber-100 rounded-lg"><Layers className="w-5 h-5 text-amber-600" /></div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Timeline + Budget details */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Timeline */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm text-slate-700 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Tidslinje" : "Timeline"}
                  </h3>
                  {dashboardData?.timeline?.start && dashboardData?.timeline?.end ? (
                    <div className="space-y-3">
                      <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all" style={{ width: `${dashboardData.timeline.progress}%` }} />
                        <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-blue-600 rounded-full shadow" style={{ left: `${dashboardData.timeline.progress}%`, transform: `translate(-50%, -50%)` }} />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>{dashboardData.timeline.start}</span>
                        <span className="font-medium text-blue-600">{language === "sv" ? "Idag" : "Today"} ({dashboardData.timeline.progress}%)</span>
                        <span>{dashboardData.timeline.end}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">{language === "sv" ? "Inga datum satta" : "No dates set"}</p>
                  )}
                </CardContent>
              </Card>

              {/* Budget breakdown */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm text-slate-700 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Budget" : "Budget"}
                  </h3>
                  {(dashboardData?.budget?.total || 0) > 0 ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{language === "sv" ? "Spenderat" : "Spent"}</span>
                        <span className="font-medium">{(dashboardData.budget.spent || 0).toLocaleString()} kr</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{language === "sv" ? "Kvar" : "Remaining"}</span>
                        <span className={`font-medium ${dashboardData.budget.remaining < 0 ? 'text-red-600' : 'text-green-600'}`}>{(dashboardData.budget.remaining || 0).toLocaleString()} kr</span>
                      </div>
                      <div className="flex justify-between text-sm border-t pt-2">
                        <span className="text-slate-600 font-medium">{language === "sv" ? "Totalbudget" : "Total budget"}</span>
                        <span className="font-bold">{(dashboardData.budget.total || 0).toLocaleString()} kr</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">{language === "sv" ? "Ingen budget satt" : "No budget set"}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Task priority breakdown */}
            {(dashboardData?.tasks?.total || 0) > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm text-slate-700 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Uppgifter per prioritet" : "Tasks by Priority"}
                  </h3>
                  <div className="flex gap-4">
                    {[
                      { key: "high", label: language === "sv" ? "Hög" : "High", color: "bg-red-500" },
                      { key: "medium", label: language === "sv" ? "Medel" : "Medium", color: "bg-amber-500" },
                      { key: "low", label: language === "sv" ? "Låg" : "Low", color: "bg-blue-400" },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="flex-1 text-center">
                        <div className={`text-2xl font-bold ${color.replace('bg-', 'text-')}`} style={{ fontFamily: 'Barlow Condensed' }}>
                          {dashboardData.tasks.by_priority?.[key] || 0}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {project?.description && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Beskrivning" : "Description"}
                  </h3>
                  <p className="text-sm text-slate-600">{project.description}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Phases Tab */}
          <TabsContent value="phases" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={phaseDialogOpen} onOpenChange={setPhaseDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-phase-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === "sv" ? "Ny etapp" : "New phase"}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{language === "sv" ? "Skapa etapp" : "Create phase"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createPhase} className="space-y-4">
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Namn" : "Name"} *</Label>
                      <Input 
                        value={phaseForm.name}
                        onChange={(e) => setPhaseForm({...phaseForm, name: e.target.value})}
                        required
                        data-testid="phase-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Beskrivning" : "Description"}</Label>
                      <Textarea 
                        value={phaseForm.description}
                        onChange={(e) => setPhaseForm({...phaseForm, description: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Start" : "Start"}</Label>
                        <Input type="date" value={phaseForm.start_date} onChange={(e) => setPhaseForm({...phaseForm, start_date: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Slut" : "End"}</Label>
                        <Input type="date" value={phaseForm.end_date} onChange={(e) => setPhaseForm({...phaseForm, end_date: e.target.value})} />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" data-testid="phase-submit-btn">
                      {language === "sv" ? "Skapa" : "Create"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {phases.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">
                    {language === "sv" ? "Inga etapper ännu" : "No phases yet"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {phases.map((phase) => {
                  const phaseActivities = activities.filter(a => a.phase_id === phase.phase_id);
                  return (
                    <Card key={phase.phase_id} data-testid={`phase-card-${phase.phase_id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>{phase.name}</CardTitle>
                            <Badge className={statusColors[phase.status]}>{phase.status}</Badge>
                          </div>
                          <Dialog open={activityDialogOpen && selectedPhase === phase.phase_id} onOpenChange={(open) => {
                            setActivityDialogOpen(open);
                            if (open) setSelectedPhase(phase.phase_id);
                          }}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`add-activity-btn-${phase.phase_id}`}>
                                <Plus className="w-4 h-4 mr-1" />
                                {language === "sv" ? "Aktivitet" : "Activity"}
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>{language === "sv" ? "Skapa aktivitet" : "Create activity"}</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={createActivity} className="space-y-4">
                                <div className="space-y-2">
                                  <Label>{language === "sv" ? "Namn" : "Name"} *</Label>
                                  <Input 
                                    value={activityForm.name}
                                    onChange={(e) => setActivityForm({...activityForm, name: e.target.value})}
                                    required
                                    data-testid="activity-name-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>{language === "sv" ? "Planerade timmar" : "Estimated hours"}</Label>
                                  <Input 
                                    type="number"
                                    value={activityForm.estimated_hours}
                                    onChange={(e) => setActivityForm({...activityForm, estimated_hours: parseFloat(e.target.value) || 0})}
                                  />
                                </div>
                                <Button type="submit" className="w-full" data-testid="activity-submit-btn">
                                  {language === "sv" ? "Skapa" : "Create"}
                                </Button>
                              </form>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {phase.description && (
                          <p className="text-slate-600 text-sm mb-4">{phase.description}</p>
                        )}
                        {phaseActivities.length > 0 && (
                          <div className="space-y-2">
                            {phaseActivities.map((activity) => (
                              <div 
                                key={activity.activity_id}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-slate-900">{activity.name}</p>
                                  {activity.estimated_hours > 0 && (
                                    <p className="text-sm text-slate-500">{activity.estimated_hours}h</p>
                                  )}
                                </div>
                                <Badge className={statusColors[activity.status]}>{activity.status}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-4">
            <div className="flex justify-end">
              {activities.length > 0 && (
                <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-task-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      {language === "sv" ? "Ny uppgift" : "New task"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{language === "sv" ? "Skapa uppgift" : "Create task"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createTask} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Aktivitet" : "Activity"} *</Label>
                        <Select value={selectedActivity || ""} onValueChange={setSelectedActivity}>
                          <SelectTrigger data-testid="task-activity-select">
                            <SelectValue placeholder={language === "sv" ? "Välj aktivitet" : "Select activity"} />
                          </SelectTrigger>
                          <SelectContent>
                            {activities.map(a => (
                              <SelectItem key={a.activity_id} value={a.activity_id}>{a.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Namn" : "Name"} *</Label>
                        <Input 
                          value={taskForm.name}
                          onChange={(e) => setTaskForm({...taskForm, name: e.target.value})}
                          required
                          data-testid="task-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Prioritet" : "Priority"}</Label>
                        <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">{language === "sv" ? "Låg" : "Low"}</SelectItem>
                            <SelectItem value="medium">{language === "sv" ? "Medium" : "Medium"}</SelectItem>
                            <SelectItem value="high">{language === "sv" ? "Hög" : "High"}</SelectItem>
                            <SelectItem value="critical">{language === "sv" ? "Kritisk" : "Critical"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === "sv" ? "Förfallodatum" : "Due date"}</Label>
                        <Input 
                          type="date"
                          value={taskForm.due_date}
                          onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={!selectedActivity} data-testid="task-submit-btn">
                        {language === "sv" ? "Skapa" : "Create"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {tasks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">
                    {activities.length === 0 
                      ? (language === "sv" ? "Skapa aktiviteter först" : "Create activities first")
                      : (language === "sv" ? "Inga uppgifter ännu" : "No tasks yet")}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => (
                  <Card key={task.task_id} className="hover:shadow-sm transition-shadow cursor-pointer" data-testid={`task-card-${task.task_id}`}
                    onClick={() => openTaskDetail(task)}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={task.status === "completed"}
                          onCheckedChange={(e) => { e?.stopPropagation?.(); toggleTaskStatus(task.task_id, task.status); }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`task-checkbox-${task.task_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${task.status === "completed" ? "line-through text-slate-400" : "text-slate-900"}`}>
                              {task.name}
                            </p>
                            <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                          </div>
                          {task.due_date && (
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              {task.due_date}
                            </p>
                          )}
                        </div>
                        <MessageSquare className="w-4 h-4 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4" data-testid="activity-tab-content">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Senaste händelser" : "Recent Events"}
              </h3>
              <Button variant="outline" size="sm" onClick={fetchActivityLog} data-testid="refresh-activity-btn">
                <Activity className="w-4 h-4 mr-1" />
                {language === "sv" ? "Uppdatera" : "Refresh"}
              </Button>
            </div>

            {activityLog.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">
                    {language === "sv" ? "Ingen aktivitet registrerad ännu" : "No activity recorded yet"}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {language === "sv" ? "Händelser visas här när uppgifter, avvikelser eller ritningar skapas" : "Events will appear here when tasks, incidents or drawings are created"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="relative">
                <div className="absolute left-[19px] top-0 bottom-0 w-px bg-slate-200" />
                <div className="space-y-0">
                  {activityLog.map((log, i) => {
                    const actionLabels = {
                      created: { sv: "skapade", en: "created", color: "bg-green-500" },
                      updated: { sv: "uppdaterade", en: "updated", color: "bg-blue-500" },
                      completed: { sv: "slutförde", en: "completed", color: "bg-emerald-500" },
                      deleted: { sv: "raderade", en: "deleted", color: "bg-red-500" },
                      uploaded: { sv: "laddade upp", en: "uploaded", color: "bg-purple-500" },
                      commented: { sv: "kommenterade på", en: "commented on", color: "bg-indigo-500" },
                    };
                    const entityLabels = {
                      task: { sv: "uppgift", en: "task", Icon: CheckCircle2 },
                      phase: { sv: "etapp", en: "phase", Icon: Layers },
                      incident: { sv: "avvikelse", en: "incident", Icon: AlertTriangle },
                      drawing: { sv: "ritning", en: "drawing", Icon: FileText },
                      drawing_version: { sv: "ritningsversion", en: "drawing version", Icon: FileText },
                      activity: { sv: "aktivitet", en: "activity", Icon: FolderKanban },
                    };
                    const actionCfg = actionLabels[log.action] || { sv: log.action, en: log.action, color: "bg-slate-400" };
                    const entityCfg = entityLabels[log.entity_type] || { sv: log.entity_type, en: log.entity_type, Icon: Circle };
                    const EntityIcon = entityCfg.Icon;
                    const timeAgo = getTimeAgo(log.created_at, language);

                    return (
                      <div key={log.activity_id || i} className="relative flex items-start gap-4 py-3 pl-1" data-testid={`activity-item-${i}`}>
                        <div className={`relative z-10 w-[38px] h-[38px] rounded-full flex items-center justify-center shrink-0 ${actionCfg.color}`}>
                          <EntityIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                          <p className="text-sm text-slate-800">
                            <span className="font-medium">{log.user_name || (language === "sv" ? "Användare" : "User")}</span>
                            {" "}{language === "sv" ? actionCfg.sv : actionCfg.en}{" "}
                            {language === "sv" ? entityCfg.sv : entityCfg.en}{" "}
                            <span className="font-medium">"{log.entity_name}"</span>
                            {log.detail && <span className="text-slate-500"> — {log.detail}</span>}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">{timeAgo}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4" data-testid="team-tab-content">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Projektmedlemmar" : "Project Members"}
              </h3>
            </div>

            {/* Add Member */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Lägg till medarbetare" : "Add Member"}
                </h4>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={addMemberSearch}
                      onChange={(e) => setAddMemberSearch(e.target.value)}
                      placeholder={language === "sv" ? "Sök namn eller e-post..." : "Search name or email..."}
                      className="h-9 text-sm"
                      data-testid="add-member-search"
                    />
                    {addMemberSearch.length > 0 && filteredAvailableUsers.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white rounded-lg shadow-lg border border-slate-200 max-h-[180px] overflow-y-auto" data-testid="add-member-dropdown">
                        {filteredAvailableUsers.slice(0, 6).map(u => (
                          <button
                            key={u.user_id}
                            type="button"
                            onClick={() => addProjectMember(u.user_id)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                            data-testid={`add-member-option-${u.user_id}`}
                          >
                            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600">
                              {u.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-800 block truncate">{u.name}</span>
                              <span className="text-xs text-slate-400 block truncate">{u.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {addMemberSearch.length > 0 && filteredAvailableUsers.length === 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 w-full bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2">
                        <p className="text-sm text-slate-400">{language === "sv" ? "Inga matchande användare" : "No matching users"}</p>
                      </div>
                    )}
                  </div>
                  <select
                    value={addMemberRole}
                    onChange={(e) => setAddMemberRole(e.target.value)}
                    className="h-9 text-sm border rounded-md px-2 bg-white text-slate-700"
                    data-testid="add-member-role-select"
                  >
                    <option value="worker">{language === "sv" ? "Medarbetare" : "Worker"}</option>
                    <option value="project_manager">{language === "sv" ? "Projektledare" : "PM"}</option>
                    <option value="site_manager">{language === "sv" ? "Platschef" : "Site Mgr"}</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Members List */}
            {projectMembers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{language === "sv" ? "Inga medarbetare i projektet ännu" : "No members in this project yet"}</p>
                  <p className="text-sm text-slate-400 mt-1">{language === "sv" ? "Använd sökfältet ovan för att lägga till" : "Use the search above to add members"}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {projectMembers.map(m => {
                  const roleLabels = {
                    worker: language === "sv" ? "Medarbetare" : "Worker",
                    project_manager: language === "sv" ? "Projektledare" : "Project Manager",
                    site_manager: language === "sv" ? "Platschef" : "Site Manager",
                    admin: "Admin"
                  };
                  const roleColors = {
                    project_manager: "bg-blue-100 text-blue-800",
                    site_manager: "bg-purple-100 text-purple-800",
                    worker: "bg-slate-100 text-slate-700",
                    admin: "bg-amber-100 text-amber-800"
                  };
                  return (
                    <Card key={m.member_id} className="hover:shadow-sm transition-shadow" data-testid={`project-member-${m.user_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                            {m.user_name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{m.user_name || m.user_email}</p>
                            <p className="text-xs text-slate-400 truncate">{m.user_email}</p>
                            <Badge className={`mt-1 text-xs ${roleColors[m.role] || "bg-slate-100 text-slate-700"}`}>
                              {roleLabels[m.role] || m.role}
                            </Badge>
                          </div>
                          <button
                            onClick={() => removeProjectMember(m.user_id)}
                            className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            title={language === "sv" ? "Ta bort" : "Remove"}
                            data-testid={`remove-project-member-${m.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            {/* PDF Reports */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "PDF-rapporter" : "PDF Reports"}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">
                          {language === "sv" ? "Projektrapport" : "Project Report"}
                        </h4>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {language === "sv" ? "Komplett projektöversikt med etapper, uppgifter, avvikelser och kostnader" : "Complete project overview with phases, tasks, incidents and costs"}
                        </p>
                        <Button
                          size="sm"
                          className="mt-3"
                          onClick={() => downloadReport("pdf", `projektrapport_${projectId}.pdf`, "project-pdf")}
                          disabled={exporting === "project-pdf"}
                          data-testid="export-project-pdf-btn"
                        >
                          {exporting === "project-pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                          {language === "sv" ? "Ladda ner PDF" : "Download PDF"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Shield className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">
                          {language === "sv" ? "KMA-rapport" : "KMA Report"}
                        </h4>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {language === "sv" ? "Riskbedömningar, skyddsronder och avvikelser" : "Risk assessments, safety rounds and incidents"}
                        </p>
                        <Button
                          size="sm"
                          className="mt-3"
                          onClick={() => downloadReport("kma-pdf", `kma_rapport_${projectId}.pdf`, "kma-pdf")}
                          disabled={exporting === "kma-pdf"}
                          data-testid="export-kma-pdf-btn"
                        >
                          {exporting === "kma-pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                          {language === "sv" ? "Ladda ner PDF" : "Download PDF"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Period Report */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Periodrapport" : "Period Report"}
              </h3>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">
                        {language === "sv" ? "Periodrapport" : "Period Report"}
                      </h4>
                      <p className="text-sm text-slate-500 mt-0.5 mb-3">
                        {language === "sv" ? "Dagrapporter, tidrapporter och avvikelser för vald period" : "Daily reports, time entries and incidents for selected period"}
                      </p>
                      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">{language === "sv" ? "Från" : "From"}</Label>
                          <Input
                            type="date"
                            value={periodStart}
                            onChange={(e) => setPeriodStart(e.target.value)}
                            className="w-40"
                            data-testid="period-start-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">{language === "sv" ? "Till" : "To"}</Label>
                          <Input
                            type="date"
                            value={periodEnd}
                            onChange={(e) => setPeriodEnd(e.target.value)}
                            className="w-40"
                            data-testid="period-end-input"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={downloadPeriodReport}
                          disabled={exporting === "period" || !periodStart || !periodEnd}
                          data-testid="export-period-pdf-btn"
                        >
                          {exporting === "period" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                          {language === "sv" ? "Ladda ner PDF" : "Download PDF"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Excel Exports */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Excel-export" : "Excel Exports"}
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 text-center">
                    <div className="p-2 bg-green-100 rounded-lg w-fit mx-auto mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    </div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      {language === "sv" ? "Tidrapporter" : "Time Entries"}
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => downloadReport("time-entries-excel", `tidrapporter_${projectId}.xlsx`, "time-excel")}
                      disabled={exporting === "time-excel"}
                      data-testid="export-time-excel-btn"
                    >
                      {exporting === "time-excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      .xlsx
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 text-center">
                    <div className="p-2 bg-red-100 rounded-lg w-fit mx-auto mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-red-600" />
                    </div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      {language === "sv" ? "Avvikelser" : "Incidents"}
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => downloadReport("incidents-excel", `avvikelser_${projectId}.xlsx`, "incidents-excel")}
                      disabled={exporting === "incidents-excel"}
                      data-testid="export-incidents-excel-btn"
                    >
                      {exporting === "incidents-excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      .xlsx
                    </Button>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 text-center">
                    <div className="p-2 bg-amber-100 rounded-lg w-fit mx-auto mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                    </div>
                    <h4 className="font-medium text-slate-900 text-sm">
                      {language === "sv" ? "Kostnader" : "Costs"}
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => downloadReport("costs-excel", `kostnader_${projectId}.xlsx`, "costs-excel")}
                      disabled={exporting === "costs-excel"}
                      data-testid="export-costs-excel-btn"
                    >
                      {exporting === "costs-excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      .xlsx
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Weekly Report */}
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" ? "Veckorapport via e-post" : "Weekly Report via Email"}
              </h3>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-indigo-100 rounded-lg">
                      <Mail className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">
                            {language === "sv" ? "Automatisk veckorapport" : "Automatic Weekly Report"}
                          </h4>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {language === "sv"
                              ? "Sammanfattning av veckans uppgifter, timmar, avvikelser och kostnader skickas till projektledare och admin"
                              : "Summary of tasks, hours, incidents and costs sent to project managers and admins"}
                          </p>
                        </div>
                        <Switch
                          checked={weeklyEnabled}
                          onCheckedChange={toggleWeeklyReport}
                          disabled={weeklyLoading}
                          data-testid="weekly-report-toggle"
                        />
                      </div>

                      {weeklyLastSent && (
                        <p className="text-xs text-slate-400">
                          {language === "sv" ? "Senast skickad: " : "Last sent: "}
                          {new Date(weeklyLastSent).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      )}

                      {/* Recipients */}
                      {weeklyRecipients.length > 0 && (
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="text-xs font-medium text-slate-600">
                              {language === "sv" ? "Mottagare" : "Recipients"} ({weeklyRecipients.length})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {weeklyRecipients.map((r, i) => (
                              <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white border border-slate-200 text-slate-700" data-testid={`recipient-${i}`}>
                                {r.name || r.email}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={sendWeeklyReportNow}
                        disabled={sendingWeekly}
                        data-testid="send-weekly-report-btn"
                      >
                        {sendingWeekly ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        {sendingWeekly
                          ? (language === "sv" ? "Skickar..." : "Sending...")
                          : (language === "sv" ? "Skicka veckorapport nu" : "Send weekly report now")}
                      </Button>

                      {/* Scheduler info */}
                      {schedulerStatus && (
                        <div className="bg-slate-50 rounded-lg p-3 mt-3 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${schedulerStatus.running ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="text-xs font-medium text-slate-600" data-testid="scheduler-status">
                              {schedulerStatus.running
                                ? (language === "sv" ? "Schemaläggare aktiv" : "Scheduler active")
                                : (language === "sv" ? "Schemaläggare inaktiv" : "Scheduler inactive")}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500" data-testid="scheduler-schedule">
                            {schedulerStatus.schedule}
                          </p>
                          <p className="text-xs text-slate-500" data-testid="scheduler-next">
                            {language === "sv" ? "Nästa utskick: " : "Next scheduled: "}
                            {schedulerStatus.next_scheduled
                              ? new Date(schedulerStatus.next_scheduled).toLocaleDateString("sv-SE", { weekday: "long", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                              : "-"}
                          </p>
                        </div>
                      )}

                      {/* Logs */}
                      {weeklyLogs.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-xs font-medium text-slate-500 mb-2">
                            {language === "sv" ? "Senaste utskick" : "Recent sends"} ({weeklyLogs.length})
                          </h5>
                          <div className="space-y-1 max-h-[120px] overflow-y-auto">
                            {weeklyLogs.slice(0, 5).map((log, i) => (
                              <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white rounded border border-slate-100" data-testid={`weekly-log-${i}`}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full ${log.status === 'success' ? 'bg-green-500' : log.status === 'error' ? 'bg-red-500' : 'bg-amber-500'}`} />
                                  <span className="text-slate-600">v.{log.week}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    {log.trigger === "scheduled" ? (language === "sv" ? "Auto" : "Auto") : (language === "sv" ? "Manuell" : "Manual")}
                                  </Badge>
                                </div>
                                <span className="text-slate-400">
                                  {new Date(log.sent_at).toLocaleDateString("sv-SE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Task Detail Dialog */}
        <Dialog open={taskDetailOpen} onOpenChange={(open) => { setTaskDetailOpen(open); if (!open) setSelectedTask(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <CheckCircle2 className={`w-5 h-5 ${selectedTask?.status === "completed" ? "text-green-500" : "text-slate-400"}`} />
                {selectedTask?.name}
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Task meta */}
              <div className="flex flex-wrap gap-2">
                <Badge className={priorityColors[selectedTask?.priority]}>{selectedTask?.priority}</Badge>
                <Badge className={statusColors[selectedTask?.status] || "bg-slate-100 text-slate-800"}>{selectedTask?.status}</Badge>
                {selectedTask?.due_date && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Calendar className="w-3 h-3" /> {selectedTask.due_date}
                  </span>
                )}
              </div>

              {selectedTask?.description && (
                <p className="text-sm text-slate-600">{selectedTask.description}</p>
              )}

              {/* Comments section */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  <MessageSquare className="w-4 h-4" />
                  {language === "sv" ? "Kommentarer" : "Comments"} ({comments.length})
                </h4>

                {commentsLoading ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4" data-testid="no-comments-text">
                    {language === "sv" ? "Inga kommentarer ännu. Bli den första!" : "No comments yet. Be the first!"}
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[250px] overflow-y-auto" data-testid="comments-list">
                    {comments.map((c) => (
                      <div key={c.comment_id} className="group flex gap-3" data-testid={`comment-${c.comment_id}`}>
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-xs font-medium text-slate-600">
                          {c.user_name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{c.user_name}</span>
                            <span className="text-xs text-slate-400">{getTimeAgo(c.created_at, language)}</span>
                            {c.user_id === user?.user_id && (
                              <button
                                onClick={() => deleteComment(c.comment_id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                                data-testid={`delete-comment-${c.comment_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-line">{renderCommentText(c.text)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Comment input */}
                <div className="relative mt-3">
                  {mentionOpen && filteredMentions.length > 0 && (
                    <div className="absolute bottom-full mb-1 left-0 w-full bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[160px] overflow-y-auto" data-testid="mention-dropdown">
                      {filteredMentions.map((mu, i) => (
                        <button
                          key={mu.user_id}
                          type="button"
                          onClick={() => insertMention(mu)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${i === mentionIndex ? "bg-blue-50" : "hover:bg-slate-50"}`}
                          data-testid={`mention-option-${i}`}
                        >
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                            {mu.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-slate-800">{mu.name}</span>
                            <span className="text-xs text-slate-400 ml-2 capitalize">{mu.role?.replace("_", " ")}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <form onSubmit={submitComment} className="flex gap-2" data-testid="comment-form">
                    <Input
                      ref={commentInputRef}
                      value={newComment}
                      onChange={handleCommentInput}
                      onKeyDown={handleCommentKeyDown}
                      placeholder={language === "sv" ? "Skriv en kommentar... (@ för att nämna)" : "Write a comment... (@ to mention)"}
                      className="flex-1"
                      data-testid="comment-input"
                    />
                    <Button type="submit" size="sm" disabled={!newComment.trim() || commentSubmitting} data-testid="comment-submit-btn">
                      {commentSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};
